'use client';

import { useState, useEffect } from 'react';

function detectBrowser(): { name: string; supported: boolean; warning: string | null } {
  if (typeof window === 'undefined') return { name: 'unknown', supported: true, warning: null };
  const ua = navigator.userAgent;
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)) {
    return { name: 'Chrome', supported: true, warning: null };
  }
  if (/Edg\//.test(ua)) {
    return { name: 'Edge', supported: true, warning: null };
  }
  if (/Firefox\//.test(ua)) {
    return {
      name: 'Firefox',
      supported: false,
      warning: 'Firefox has limited Web Worker support. Large file processing may be slower or fail. For best results, use Chrome.',
    };
  }
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    return {
      name: 'Safari',
      supported: false,
      warning: 'Safari has limited support for the Web Workers TurboSheet relies on. Some features may not work correctly. We recommend Chrome.',
    };
  }
  return {
    name: 'Unknown',
    supported: false,
    warning: 'Your browser may not fully support TurboSheet\'s local processing features. For best results, use Google Chrome.',
  };
}

export default function BrowserCompatibilityWarning() {
  const [dismissed, setDismissed] = useState(true);
  const [browser, setBrowser] = useState<ReturnType<typeof detectBrowser> | null>(null);

  useEffect(() => {
    const detected = detectBrowser();
    setBrowser(detected);
    if (!detected.supported) {
      const wasDismissed = sessionStorage.getItem('browser-warning-dismissed');
      if (!wasDismissed) setDismissed(false);
    }
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('browser-warning-dismissed', '1');
    setDismissed(true);
  };

  if (dismissed || !browser || browser.supported) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-full max-w-xl px-4">
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-[#f59e0b]/10 border border-[#f59e0b]/30 backdrop-blur-xl shadow-2xl">
        <span className="text-xl flex-shrink-0 mt-0.5">⚠️</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#f59e0b] mb-0.5">{browser.name} detected</p>
          <p className="text-xs text-white/60 leading-relaxed">{browser.warning}</p>
          <a
            href="https://www.google.com/chrome/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-xs text-[#0ea5e9] hover:underline font-medium"
          >
            Download Chrome →
          </a>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 w-6 h-6 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/50 hover:text-white transition-all text-xs"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
